// src/lib/metrics/distribution.ts
/**
 * Distribution metrics - skewness, kurtosis, quantiles, z-scores, percentiles
 */

import type { Series, DistributionMetrics } from './types';
import { filterValid, mean, stdDev, median as calcMedian } from './primitives';

// ============================================================================
// HIGHER MOMENTS
// ============================================================================

/**
 * Calculate skewness (third standardized moment)
 * Measures asymmetry of the distribution
 * 
 * - Positive skew: right tail longer (more extreme gains)
 * - Negative skew: left tail longer (more extreme losses)
 * 
 * @returns Skewness value, or null if insufficient data
 */
export function skewness(values: Series): number | null {
  const valid = filterValid(values);
  if (valid.length < 3) return null;
  
  const m = mean(valid);
  const s = stdDev(valid);
  
  if (m === null || s === null || s === 0) return null;
  
  const n = valid.length;
  let m3 = 0;
  
  for (const v of valid) {
    const d = v - m;
    m3 += Math.pow(d, 3);
  }
  
  m3 /= n;
  
  return m3 / Math.pow(s, 3);
}

/**
 * Calculate excess kurtosis (fourth standardized moment - 3)
 * Measures tail heaviness relative to normal distribution
 * 
 * - Positive (leptokurtic): fat tails, more extreme events
 * - Negative (platykurtic): thin tails, fewer extremes
 * - Zero (mesokurtic): normal distribution-like
 * 
 * @returns Excess kurtosis, or null if insufficient data
 */
export function kurtosis(values: Series): number | null {
  const valid = filterValid(values);
  if (valid.length < 4) return null;
  
  const m = mean(valid);
  const s = stdDev(valid);
  
  if (m === null || s === null || s === 0) return null;
  
  const n = valid.length;
  let m4 = 0;
  
  for (const v of valid) {
    const d = v - m;
    m4 += Math.pow(d, 4);
  }
  
  m4 /= n;
  const s4 = Math.pow(s, 4);
  
  // Return excess kurtosis (Fisher's definition)
  return (m4 / s4) - 3;
}

/**
 * Calculate all distribution metrics at once
 */
export function computeDistributionMetrics(values: Series): DistributionMetrics {
  return {
    skewness: skewness(values),
    kurtosis: kurtosis(values),
    mean: mean(values),
    median: calcMedian(values),
    stdDev: stdDev(values),
  };
}

// ============================================================================
// QUANTILES & PERCENTILES
// ============================================================================

/**
 * Calculate quantile (value at given probability)
 * 
 * @param values - Array of values
 * @param p - Probability (0-1), e.g., 0.5 for median
 * @returns Value at quantile
 */
export function quantile(values: Series, p: number): number | null {
  const valid = filterValid(values);
  if (valid.length === 0 || p < 0 || p > 1) return null;
  
  const sorted = [...valid].sort((a, b) => a - b);
  
  // Linear interpolation
  const idx = p * (sorted.length - 1);
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  
  if (lower === upper) {
    return sorted[lower]!;
  }
  
  const weight = idx - lower;
  return sorted[lower]! * (1 - weight) + sorted[upper]! * weight;
}

/**
 * Calculate percentile of a value within a sample
 * What percentage of the sample is below this value?
 * 
 * @param value - Value to find percentile for
 * @param sample - Reference sample
 * @returns Percentile (0-100)
 */
export function percentileRank(value: number, sample: Series): number | null {
  const valid = filterValid(sample);
  if (valid.length === 0 || !Number.isFinite(value)) return null;
  
  const sorted = [...valid].sort((a, b) => a - b);
  
  // Count values below
  let below = 0;
  for (const v of sorted) {
    if (v < value) below++;
  }
  
  return (below / sorted.length) * 100;
}

/**
 * Calculate multiple quantiles (quartiles, deciles, etc.)
 */
export function quantiles(
  values: Series,
  probabilities: number[]
): (number | null)[] {
  return probabilities.map(p => quantile(values, p));
}

/**
 * Calculate quartiles (Q1, Q2/median, Q3)
 */
export function quartiles(values: Series): {
  q1: number | null;
  median: number | null;
  q3: number | null;
} {
  return {
    q1: quantile(values, 0.25),
    median: quantile(values, 0.5),
    q3: quantile(values, 0.75),
  };
}

/**
 * Calculate interquartile range (Q3 - Q1)
 */
export function iqr(values: Series): number | null {
  const q = quartiles(values);
  if (q.q1 === null || q.q3 === null) return null;
  return q.q3 - q.q1;
}

/**
 * Five-number summary (min, Q1, median, Q3, max)
 */
export function fiveNumberSummary(values: Series): {
  min: number | null;
  q1: number | null;
  median: number | null;
  q3: number | null;
  max: number | null;
} {
  const valid = filterValid(values);
  if (valid.length === 0) {
    return { min: null, q1: null, median: null, q3: null, max: null };
  }
  
  const sorted = [...valid].sort((a, b) => a - b);
  
  return {
    min: sorted[0]!,
    q1: quantile(valid, 0.25),
    median: quantile(valid, 0.5),
    q3: quantile(valid, 0.75),
    max: sorted[sorted.length - 1]!,
  };
}

// ============================================================================
// Z-SCORES & STANDARDIZATION
// ============================================================================

/**
 * Calculate z-score for a single value
 * Number of standard deviations from the mean
 */
export function zScore(value: number, values: Series): number | null {
  const m = mean(values);
  const s = stdDev(values);
  
  if (m === null || s === null || s === 0 || !Number.isFinite(value)) {
    return null;
  }
  
  return (value - m) / s;
}

/**
 * Calculate z-scores for all values in a series
 */
export function zScores(values: Series): number[] {
  const valid = filterValid(values);
  const m = mean(valid);
  const s = stdDev(valid);
  
  if (m === null || s === null || s === 0) {
    return valid.map(() => NaN);
  }
  
  return valid.map(v => (v - m) / s);
}

/**
 * Standardize a series to z-scores (preserving nulls)
 */
export function standardize(values: Series): (number | null)[] {
  const valid = filterValid(values);
  const m = mean(valid);
  const s = stdDev(valid);
  
  if (m === null || s === null || s === 0) {
    return values.map(() => null);
  }
  
  return values.map(v => {
    if (v === null || v === undefined || !Number.isFinite(v)) {
      return null;
    }
    return (v - m) / s;
  });
}

// ============================================================================
// COEFFICIENT OF VARIATION
// ============================================================================

/**
 * Calculate coefficient of variation (CV)
 * Relative standard deviation as percentage of mean
 * Useful for comparing variability across different scales
 * 
 * @returns CV as decimal (multiply by 100 for percentage)
 */
export function coefficientOfVariation(values: Series): number | null {
  const m = mean(values);
  const s = stdDev(values);
  
  if (m === null || s === null || m === 0) {
    return null;
  }
  
  return Math.abs(s / m);
}

/**
 * Calculate CV as percentage
 */
export function cvPercent(values: Series): number | null {
  const cv = coefficientOfVariation(values);
  return cv !== null ? cv * 100 : null;
}

// ============================================================================
// NORMALITY TESTS (Simple)
// ============================================================================

/**
 * Jarque-Bera test statistic for normality
 * Higher values indicate departure from normality
 * 
 * JB = (n/6) * (S² + (K²/4))
 * where S = skewness, K = excess kurtosis
 */
export function jarqueBeraStatistic(values: Series): number | null {
  const valid = filterValid(values);
  const n = valid.length;
  
  if (n < 8) return null; // Need enough data
  
  const s = skewness(valid);
  const k = kurtosis(valid);
  
  if (s === null || k === null) return null;
  
  return (n / 6) * (Math.pow(s, 2) + Math.pow(k, 2) / 4);
}

